import { ReactNode, useState } from 'react';

import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

import AppBar from './AppBar';
import InAppBanner from './InAppBanner';
import NavigationMenu from './NavigationMenu';
import AccessDenied from '../AccessDenied';
import { useUserStateContext } from '../UserStateProvider';
import { Group, Panel, useDefaultLayout } from 'react-resizable-panels';
import GripSeparator from '../GripSeparator';

type Props = {
  children: ReactNode;
  title: string;
  sideContent?: () => ReactNode;
  renderSummaryData?: () => ReactNode;
  pageMenuWidth?: number;
  requiredPermissions?: string[];
};

const AppPageWithSideContent = ({
  children,
  title,
  sideContent,
  renderSummaryData,
  requiredPermissions,
}: Props) => {
  const [navigationAnchorEl, setNavigationAnchorEl] = useState<HTMLElement | null>(null);
  const { hasRole, isLoading } = useUserStateContext();

  // Detect mobile/tablet devices - use vertical layout for narrow screens or portrait orientation
  const isMobileOrPortrait = useMediaQuery('(max-width: 768px), (orientation: portrait)');
  const orientation = isMobileOrPortrait ? 'vertical' : 'horizontal';
  const seperatorOrientation = isMobileOrPortrait ? 'horizontal' : 'vertical';

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'app-page-sidecontent',
    storage: localStorage,
  });

  if (!hasRole || isLoading) {
    return <></>;
  }

  if (requiredPermissions && !hasRole(requiredPermissions)) {
    return <AccessDenied />;
  }

  return (
    // The banner spans both panels, so it sits above the Group. The Group takes
    // the height left under it rather than a full 100vh of its own, which would
    // push the page past the fold whenever the banner is showing.
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <InAppBanner />
      <Group
        style={{ flex: 1 }}
        orientation={orientation}
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel id="app-page-main" style={{ display: 'flex', flexDirection: 'column' }}>
          <NavigationMenu anchorEl={navigationAnchorEl} onClose={() => setNavigationAnchorEl(null)} />
          <AppBar
            title={title}
            navigationAnchorEl={navigationAnchorEl}
            setNavigationAnchorEl={setNavigationAnchorEl}
            renderSummaryData={renderSummaryData}
          />
          <Box
            component="main"
            sx={(theme) => ({
              flex: 1,
              transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            })}
          >
            {children}
          </Box>
        </Panel>
        <GripSeparator orientation={seperatorOrientation} />
        <Panel id="app-page-side" maxSize="50%" minSize="20%" defaultSize={450} style={{ display: 'flex', flexDirection: 'column' }}>{sideContent && sideContent()}</Panel>
      </Group>
    </Box>
  );
};

export default AppPageWithSideContent;
